import { codeFrameText } from "@/components/shapes/code-frame/code-content";
import {
  docMarkdown,
  docPlainText,
  isDocContent,
} from "@/components/shapes/doc-frame/doc-content";
import { markdownToPlainText } from "@/lib/markdown-plain-text";

/** Longest excerpt a card shows. */
const EXCERPT_MAX_CHARS = 240;
/** Raw HTML/markdown read before stripping, so huge documents stay cheap. */
const SOURCE_MAX_CHARS = 4000;

const GENERIC_TEXT_FIELDS = [
  "summary",
  "subtitle",
  "description",
  "insight",
  "text",
  "body",
  "snippet",
  "message",
  "query",
  "url",
] as const;

const DOC_PATH_TYPES = new Set(["doc", "note", "markdown"]);

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Spaces collapse within a line; each paragraph keeps its own line. */
function collapse(value: string): string {
  const tidy = value
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
  return tidy.length > EXCERPT_MAX_CHARS
    ? `${tidy.slice(0, EXCERPT_MAX_CHARS - 1).trimEnd()}…`
    : tidy;
}

/** A page's text on one line, as its card carries it (`frame_card.html_text`). */
function htmlText(html: string): string {
  return html
    .slice(0, SOURCE_MAX_CHARS)
    .replace(/<(script|style|head|title)\b[\s\S]*?(<\/\1>|$)/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ");
}

/**
 * Docs (V4 content or card), legacy notes and markdown frames read through
 * the shared doc-content helpers. The first `#`/`##` heading is dropped
 * because the card's title shows it.
 */
function docText(frameType: string, source: Record<string, unknown>): string {
  const plain = docPlainText(frameType, source).slice(0, SOURCE_MAX_CHARS);
  const heading = docMarkdown(frameType, source)
    .slice(0, SOURCE_MAX_CHARS)
    .match(/^#{1,2}\s+.+$/m)?.[0];
  if (!heading) return plain;
  const title = markdownToPlainText(heading);
  const lines = plain.split("\n");
  const titleLine = lines.findIndex((line) => line.trim() === title);
  if (titleLine >= 0) lines.splice(titleLine, 1);
  return lines.join("\n");
}

function itemsText(items: unknown): string {
  if (!Array.isArray(items)) return "";
  return items
    .slice(0, 6)
    .map((item) =>
      typeof item === "object" && item !== null
        ? text((item as { text?: unknown }).text)
        : text(item),
    )
    .filter(Boolean)
    .join(" · ");
}

function firstLines(source: string, count: number): string {
  return source
    .slice(0, SOURCE_MAX_CHARS)
    .split("\n")
    .slice(0, count)
    .join(" ");
}

function typeText(frameType: string, source: Record<string, unknown>): string {
  if (DOC_PATH_TYPES.has(frameType) || isDocContent(source)) {
    return docText(frameType, source);
  }
  switch (frameType) {
    case "html":
      // Full content carries the page; its card carries the page's text.
      return text(source.html) ? htmlText(text(source.html)) : "";
    case "code":
      return firstLines(codeFrameText(source), 3);
    case "terminal":
      return text(source.command) || text(source.cwd);
    case "todo":
      return itemsText(source.items);
    default:
      return "";
  }
}

/**
 * A short plain-text excerpt of a frame for its card, one line per
 * paragraph (render it with `whitespace-pre-line`). `source` is what
 * `previewSource` picked: the card, or the full content (streaming, local
 * edits, a backend without cards), which share one schema.
 */
export function frameStaticExcerpt(
  frameType: string,
  source: Record<string, unknown>,
): string {
  const typed = typeText(frameType, source);
  if (typed.trim()) return collapse(typed);
  for (const field of GENERIC_TEXT_FIELDS) {
    const value = text(source[field]);
    if (value) return collapse(value);
  }
  return "";
}
